import React, { useEffect, useState } from 'react';
import { ArrowLeft, Play, FileText, CheckCircle2, Lock, ChevronLeft, ChevronRight, Video, Menu, X, Check } from 'lucide-react';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
interface CourseViewerProps {
  course: any;
  onBack: () => void;
}
type LessonType = 'video' | 'text';
interface Lesson {
  id: string;
  title: string;
  type: LessonType;
  duration?: string;
  isCompleted: boolean;
  isLocked: boolean;
  content?: string;
}
const generateLessons = (productId: string): Lesson[] => {
  if (productId === 'PROD-001') {
    return Array.from({
      length: 12
    }).map((_, i) => ({
      id: `l1-${i}`,
      title: `Урок ${i + 1}: ${['Введение в ведическую астрологию', 'Знаки зодиака (Раши)', 'Планеты (Грахи) и их значения', 'Дома гороскопа (Бхавы)', 'Аспекты планет (Дришти)', 'Лунные стоянки (Накшатры)', 'Построение натальной карты', 'Анализ силы планет', 'Йоги: комбинации планет', 'Периоды планет (Даши)', 'Транзиты (Гочара)', 'Практика: разбор карты'][i]}`,
      type: i % 3 === 0 ? 'text' : 'video',
      duration: i % 3 === 0 ? '10 мин чтение' : `${15 + i * 5 % 30} мин`,
      isCompleted: i < 7,
      isLocked: i > 8,
      content: i % 3 === 0 ? 'Здесь располагается подробный текстовый материал урока. В ведической астрологии (Джйотиш) мы используем сидерический зодиак...' : undefined
    }));
  }
  if (productId === 'PROD-003') {
    return Array.from({
      length: 21
    }).map((_, i) => ({
      id: `l3-${i}`,
      title: `День ${i + 1}: ${['Знакомство с энергией Венеры', 'Очищение пространства', 'Практика любви к себе', 'Финансовые блоки', 'Венера в знаках зодиака', 'Медитация на изобилие', 'Анализ отношений', 'Гармонизация Венеры', 'Стиль и красота по Венере', 'Денежная емкость', 'Работа с желаниями', 'Венера и Луна: баланс', 'Творческая энергия', 'Практика прощения', 'Венера в домах', 'Создание алтаря', 'Аффирмации', 'Ритуал красоты', 'Финансовое планирование', 'Подведение итогов', 'Интеграция опыта'][i]}`,
      type: 'video',
      duration: '15 мин',
      isCompleted: i < 14,
      isLocked: i > 15
    }));
  }
  if (productId === 'PROD-004') {
    return [{
      id: 'l4-0',
      title: 'Финансовые коды в натальной карте',
      type: 'video',
      duration: '1 ч 45 мин',
      isCompleted: false,
      isLocked: false
    }];
  }
  return Array.from({
    length: 5
  }).map((_, i) => ({
    id: `def-${i}`,
    title: `Урок ${i + 1}`,
    type: 'video',
    duration: '20 мин',
    isCompleted: i < 2,
    isLocked: i > 2
  }));
};
export function CourseViewer({
  course,
  onBack
}: CourseViewerProps) {
  const {
    showToast
  } = useToast();
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const generated = generateLessons(course.id);
    setLessons(generated);
    const firstUncompleted = generated.find(l => !l.isCompleted && !l.isLocked);
    if (firstUncompleted) {
      setActiveLessonId(firstUncompleted.id);
    } else {
      setActiveLessonId(generated[0]?.id || null);
    }
  }, [course.id]);
  const activeIndex = lessons.findIndex(l => l.id === activeLessonId);
  const activeLesson = lessons[activeIndex];
  const completedCount = lessons.filter(l => l.isCompleted).length;
  const progressPercent = Math.round(completedCount / lessons.length * 100) || 0;
  const handleLessonSelect = (lesson: Lesson) => {
    if (lesson.isLocked) {
      showToast('Этот урок пока недоступен');
      return;
    }
    setActiveLessonId(lesson.id);
    setMobileMenuOpen(false);
  };
  const handleMarkCompleted = () => {
    if (!activeLesson) return;
    setLessons(prev => prev.map(l => l.id === activeLesson.id ? {
      ...l,
      isCompleted: true
    } : l));
    showToast('Урок пройден!');
    if (activeIndex < lessons.length - 1) {
      const nextLesson = lessons[activeIndex + 1];
      if (!nextLesson.isLocked) {
        setTimeout(() => setActiveLessonId(nextLesson.id), 1000);
      }
    }
  };
  const handlePrev = () => {
    if (activeIndex > 0) {
      setActiveLessonId(lessons[activeIndex - 1].id);
    }
  };
  const handleNext = () => {
    if (activeIndex < lessons.length - 1) {
      const nextLesson = lessons[activeIndex + 1];
      if (!nextLesson.isLocked) {
        setActiveLessonId(nextLesson.id);
      } else {
        showToast('Следующий урок пока недоступен');
      }
    }
  };
  if (!activeLesson) return null;
  return <div className="flex flex-col h-full bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-19290">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50 shrink-0" data-id="element-19291">
        <div className="flex items-center gap-3 overflow-hidden" data-id="element-19292">
          <Button variant="ghost" size="sm" onClick={onBack} className="shrink-0 p-1.5 h-8" data-id="element-19293">
            <ArrowLeft className="w-4 h-4 mr-1.5" data-id="element-19294" />
            <span className="hidden sm:inline text-xs" data-id="element-19295">Назад</span>
          </Button>
          <div className="h-4 w-px bg-slate-300 shrink-0 hidden sm:block" data-id="element-19296" />
          <h2 className="text-sm font-bold text-slate-900 truncate" title={course.title} data-id="element-19297">
            {course.title}
          </h2>
        </div>
        <div className="flex items-center gap-3 shrink-0 ml-2" data-id="element-19298">
          <div className="hidden sm:flex items-center gap-2 text-xs font-medium text-slate-600 bg-white px-2.5 py-1 rounded-lg border border-slate-200" data-id="element-19299">
            <span data-id="element-19300">
              {completedCount}/{lessons.length} пройдено
            </span>
            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-19301">
              <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
              width: `${progressPercent}%`
            }} data-id="element-19302" />
            </div>
          </div>
          <Button variant="outline" size="sm" className="sm:hidden p-1.5 h-8" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-id="element-19303">
            {mobileMenuOpen ? <X className="w-4 h-4" data-id="element-19304" /> : <Menu className="w-4 h-4" data-id="element-19305" />}
          </Button>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden flex-col sm:flex-row relative" data-id="element-19306">
        {/* Sidebar (Lessons List) */}
        <div className={`
          sm:w-72 shrink-0 border-r border-slate-200 bg-slate-50/50 flex flex-col
          absolute sm:relative inset-0 z-20 sm:z-auto bg-white sm:bg-slate-50/50 transition-transform duration-300
          ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0'}
        `} data-id="element-19307">
          <div className="p-3 border-b border-slate-200 sm:hidden" data-id="element-19308">
            <div className="flex items-center justify-between mb-2" data-id="element-19309">
              <span className="text-xs font-bold text-slate-900" data-id="element-19310">
                Прогресс курса
              </span>
              <span className="text-xs font-medium text-slate-600" data-id="element-19311">
                {progressPercent}%
              </span>
            </div>
            <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-19312">
              <div className="h-full bg-indigo-500 rounded-full transition-all duration-500" style={{
              width: `${progressPercent}%`
            }} data-id="element-19313" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto custom-scrollbar p-2 space-y-1" data-id="element-19314">
            {lessons.map((lesson, idx) => {
            const isActive = lesson.id === activeLessonId;
            return <button key={lesson.id} onClick={() => handleLessonSelect(lesson)} className={`
                    w-full text-left p-2.5 rounded-xl flex items-start gap-3 transition-colors
                    ${isActive ? 'bg-indigo-50 border border-indigo-100' : 'hover:bg-slate-100 border border-transparent'}
                    ${lesson.isLocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}
                  `} data-id="element-19315">
                  <div className={`
                    w-6 h-6 rounded-full flex items-center justify-center shrink-0 mt-0.5 text-[10px] font-bold
                    ${lesson.isCompleted ? 'bg-emerald-100 text-emerald-600' : isActive ? 'bg-indigo-600 text-white' : 'bg-slate-200 text-slate-500'}
                  `} data-id="element-19316">
                    {lesson.isCompleted ? <Check className="w-3.5 h-3.5" data-id="element-19317" /> : idx + 1}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-19318">
                    <div className={`text-xs font-medium leading-tight mb-1 ${isActive ? 'text-indigo-900' : 'text-slate-700'}`} data-id="element-19319">
                      {lesson.title}
                    </div>
                    <div className="flex items-center gap-2 text-[10px] text-slate-500" data-id="element-19320">
                      <span className="flex items-center gap-1" data-id="element-19321">
                        {lesson.type === 'video' ? <Video className="w-3 h-3" data-id="element-19322" /> : <FileText className="w-3 h-3" data-id="element-19323" />}
                        {lesson.type === 'video' ? 'Видео' : 'Текст'}
                      </span>
                      {lesson.duration && <>
                          <span data-id="element-19324">•</span>
                          <span data-id="element-19325">{lesson.duration}</span>
                        </>}
                    </div>
                  </div>
                  {lesson.isLocked && <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-1" data-id="element-19326" />}
                </button>;
          })}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden bg-white relative z-10" data-id="element-19327">
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 sm:p-8" data-id="element-19328">
            <div className="max-w-3xl mx-auto" data-id="element-19329">
              {/* Lesson Header */}
              <div className="mb-6" data-id="element-19330">
                <div className="flex items-center gap-2 mb-3" data-id="element-19331">
                  <Badge variant={activeLesson.type === 'video' ? 'primary' : 'secondary'} className="text-[10px]" data-id="element-19332">
                    {activeLesson.type === 'video' ? <Video className="w-3 h-3 mr-1" data-id="element-19333" /> : <FileText className="w-3 h-3 mr-1" data-id="element-19334" />}
                    {activeLesson.type === 'video' ? 'Видеоурок' : 'Текстовый урок'}
                  </Badge>
                  {activeLesson.duration && <span className="text-xs text-slate-500" data-id="element-19335">
                      {activeLesson.duration}
                    </span>}
                </div>
                <h1 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19336">
                  {activeIndex + 1}. {activeLesson.title}
                </h1>
              </div>

              {/* Lesson Content */}
              {activeLesson.type === 'video' ? <div className="aspect-video bg-slate-900 rounded-2xl overflow-hidden relative flex items-center justify-center group mb-8 shadow-md" data-id="element-19337">
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-80" data-id="element-19338" />
                  <button className="w-16 h-16 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center text-white hover:bg-white/30 hover:scale-110 transition-all z-10" data-id="element-19339">
                    <Play className="w-6 h-6 ml-1 fill-white" data-id="element-19340" />
                  </button>
                  <div className="absolute bottom-4 left-4 right-4 flex items-center justify-between text-white/80 text-xs z-10" data-id="element-19341">
                    <span data-id="element-19342">00:00 / {activeLesson.duration || '15:00'}</span>
                    <div className="flex-1 mx-4 h-1 bg-white/30 rounded-full overflow-hidden" data-id="element-19343">
                      <div className="w-0 h-full bg-indigo-500 rounded-full" data-id="element-19344" />
                    </div>
                  </div>
                </div> : <div className="prose prose-sm sm:prose-base prose-slate max-w-none mb-8" data-id="element-19345">
                  <p className="lead text-lg text-slate-700 mb-6" data-id="element-19346">
                    {activeLesson.content || 'В этом уроке мы подробно разберем основные концепции и правила. Внимательно изучите материал перед тем как переходить к практическим заданиям.'}
                  </p>
                  <p data-id="element-19347">
                    Астрология — это древняя система знаний, которая изучает
                    связь между движением небесных тел и событиями на Земле. В
                    основе ведической астрологии (Джйотиш) лежит концепция кармы
                    и реинкарнации.
                  </p>
                  <div className="bg-indigo-50 border-l-4 border-indigo-500 p-4 my-6 rounded-r-xl" data-id="element-19348">
                    <h4 className="text-indigo-900 font-bold mt-0 mb-2" data-id="element-19349">
                      Важное замечание
                    </h4>
                    <p className="text-indigo-800 text-sm mb-0" data-id="element-19350">
                      Всегда учитывайте контекст всей карты при анализе
                      отдельных положений. Вырванные из контекста трактовки
                      могут быть ошибочными.
                    </p>
                  </div>
                  <p data-id="element-19351">
                    Для успешного освоения материала рекомендуем вести конспект
                    и сразу применять полученные знания на практике, анализируя
                    карты известных людей или своих знакомых.
                  </p>
                </div>}

              {/* Completion Action */}
              <div className="flex justify-center sm:justify-start pt-4 border-t border-slate-100" data-id="element-19352">
                {!activeLesson.isCompleted ? <Button onClick={handleMarkCompleted} className="w-full sm:w-auto" data-id="element-19353">
                    <CheckCircle2 className="w-4 h-4 mr-2" data-id="element-19354" />
                    Отметить как пройденный
                  </Button> : <div className="flex items-center gap-2 text-emerald-600 font-medium text-sm bg-emerald-50 px-4 py-2.5 rounded-xl w-full sm:w-auto justify-center" data-id="element-19355">
                    <CheckCircle2 className="w-5 h-5" data-id="element-19356" />
                    Урок пройден
                  </div>}
              </div>
            </div>
          </div>

          {/* Navigation Footer */}
          <div className="p-4 border-t border-slate-200 bg-white flex items-center justify-between shrink-0" data-id="element-19357">
            <Button variant="outline" onClick={handlePrev} disabled={activeIndex === 0} className="text-xs sm:text-sm" data-id="element-19358">
              <ChevronLeft className="w-4 h-4 sm:mr-1" data-id="element-19359" />
              <span className="hidden sm:inline" data-id="element-19360">Предыдущий</span>
            </Button>

            <div className="text-xs text-slate-500 font-medium" data-id="element-19361">
              {activeIndex + 1} из {lessons.length}
            </div>

            <Button variant="outline" onClick={handleNext} disabled={activeIndex === lessons.length - 1} className="text-xs sm:text-sm" data-id="element-19362">
              <span className="hidden sm:inline" data-id="element-19363">Следующий</span>
              <ChevronRight className="w-4 h-4 sm:ml-1" data-id="element-19364" />
            </Button>
          </div>
        </div>
      </div>
    </div>;
}